import type { Canvas, FabricObject } from 'fabric';

type HelperObject = FabricObject & { isGridLine?: boolean; isSafeZone?: boolean };

interface PreviewOptions {
  maxSide: number;
  format?: 'png' | 'jpeg';
  quality?: number;
}

// Snapshot of the canvas without the editor helpers (grid and safe zone), scaled down so its longest side
// is at most maxSide. Synchronous on purpose: it also runs while the editor is closing, right before the
// fabric canvas is disposed.
export const renderCanvasPreview = (
  canvas: Canvas | null,
  width: number,
  height: number,
  { maxSide, format = 'png', quality }: PreviewOptions
): string | null => {
  if (!canvas || canvas.disposed) return null;
  try {
    return canvas.toDataURL({
      format,
      quality,
      multiplier: Math.min(1, maxSide / Math.max(width, height, 1)),
      filter: (object) => {
        const helper = object as HelperObject;
        return !helper.isGridLine && !helper.isSafeZone;
      },
    });
  } catch {
    return null;
  }
};

const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64] = dataUrl.split(',');
  const type = /data:([^;]+)/.exec(header)?.[1] ?? 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
};

// The image stored with the canvas only feeds the list thumbnail and previews, so a 1080px JPEG is plenty
// (the canvas always has a solid background, so JPEG never loses transparency).
const STORED_PREVIEW_MAX_SIDE = 1080;
const STORED_PREVIEW_QUALITY = 0.85;

export const createCanvasPreviewFile = (canvas: Canvas | null, width: number, height: number): File | null => {
  const dataUrl = renderCanvasPreview(canvas, width, height, {
    maxSide: STORED_PREVIEW_MAX_SIDE,
    format: 'jpeg',
    quality: STORED_PREVIEW_QUALITY,
  });
  if (!dataUrl?.startsWith('data:image/')) return null;
  return new File([dataUrlToBlob(dataUrl)], 'canvas.jpg', { type: 'image/jpeg' });
};
